import React, { useState } from 'react';
import { AppBar, useDataProvider, useGetOne } from 'react-admin';
import { IconButton, Typography } from '@mui/material';
import { useLocation, useParams } from 'react-router-dom';
import CustomUserMenu from './CustomUserMenu';
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import html2canvas from "html2canvas";
import EmailIcon from "@mui/icons-material/Email";
import EmailDialog from './EmailDialog';
import { getNameAndCompanyName } from '../common';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { styled } from '@mui/material/styles';

// サイドメニューごとのタイトルマッピング
const titles = {
    '/users': 'ユーザー一覧',
    '/users/create': 'ユーザー作成',
    '/my-profile' : 'マイページ',
    '/change-password' : 'パスワード変更',
    '/customers' : '顧客検索',
    '/customers-clean' : '顧客一覧(浄化槽清掃)',
    '/customers-inspect' : '顧客一覧(浄化槽点検)',
    '/customers-construct' : '顧客一覧(浄化槽修理)',
    '/customers-garbage-commit' : '顧客一覧(ごみ収集委託)',
    '/customers-garbage-allow' : '顧客一覧(ごみ収集許可)',
    '/customers-waste' : '顧客一覧(し尿)',
    '/requesters' : '管理業者一覧',
    '/requesters/create' : '管理業者作成',
    '/cleanReceptHistory' : '清掃受付履歴',
    '/cleanReceptHistory/create' : '清掃受付履歴作成',
    '/cleanHistory' : '清掃履歴',
    '/cleanHistory/create' : '清掃履歴作成',
    '/inspectHistory' : '点検履歴',
    '/inspectHistory/create' : '点検履歴作成',
    '/constructHistory' : '修理履歴',
    '/constructHistory/create' : '修理履歴作成',
    '/contactHistory' : '問い合わせ履歴',
    '/contactHistory/create' : '問い合わせ履歴作成',
    '/visitHistory' : '訪問履歴',
    '/visitHistory/create' : '訪問履歴作成',
    '/invoiceHistory' : '請求履歴',
    '/invoiceHistory/create' : '請求履歴作成',
    '/estimateHistory' : '見積履歴',
    '/estimateHistory/create' : '見積履歴作成',
    '/uncollect-clean' : '未収一覧(浄化槽清掃)',
    '/uncollect-inspect' : '未収一覧(浄化槽点検)',
    '/uncollect-garbage-commit' : '未収一覧(ごみ収集委託)',
    '/uncollect-garbage-allow' : '未収一覧(ごみ収集許可)',
    '/uncollect-waste' : '未収一覧(し尿)',
    '/inspect' : '浄化槽点検',
    '/logText' : '操作ログ',
    '/cleanReserve' : "清掃予定"
};

const editTitles = {
    'users': 'ユーザー編集',
    'users/show': 'ユーザー表示',
    'reset-password': 'パスワードリセット',
    'customers-clean' : '顧客情報編集(浄化槽清掃)',
    'customers-clean/show' : '顧客詳細表示(浄化槽清掃)',
    'customers-inspect' : '顧客情報編集(浄化槽点検)',
    'customers-inspect/show' : '顧客詳細表示(浄化槽点検)',
    'customers-construct' : '顧客情報編集(浄化槽修理)',
    'customers-construct/show' : '顧客詳細表示(浄化槽修理)',
    'customers-garbage-commit' : '顧客情報編集(ごみ収集委託)',
    'customers-garbage-commit/show' : '顧客詳細表示(ごみ収集委託)',
    'customers-garbage-allow' : '顧客情報編集(ごみ収集許可)',
    'customers-garbage-allow/show' : '顧客詳細表示(ごみ収集許可)',
    'customers-waste' : '顧客情報編集(し尿)',
    'customers-waste/show' : '顧客詳細表示(し尿)',
    'requesters' : '管理業者編集',
    'requesters/show' : '管理業者表示',
    'cleanReceptHistory' : '清掃受付情報編集',
    'cleanReceptHistory/show' : '清掃受付情報表示',
    'cleanHistory' : '清掃情報編集',
    'cleanHistory/show' : '清掃情報表示',
    'inspectHistory' : '点検情報編集',
    'inspectHistory/show' : '点検情報表示',
    'constructHistory' : '修理情報編集',
    'constructHistory/show' : '修理情報表示',
    'contactHistory' : '問い合わせ情報編集',
    'contactHistory/show' : '問い合わせ情報表示',
    'visitHistory' : '訪問情報編集',
    'visitHistory/show' : '訪問情報表示',
    'invoiceHistory' : '請求情報編集',
    'invoiceHistory/show' : '請求情報表示',
    'estimateHistory' : '見積情報編集',
    'estimateHistory/show' : '見積情報表示',
    'uncollect-clean' : '請求情報編集',
    'uncollect-inspect' : '請求情報編集',
    'uncollect-garbage-commit' : '請求情報編集',
    'uncollect-garbage-allow' : '請求情報編集',
    'uncollect-waste' : '請求情報編集',

};

const HeaderBar = (props) => {
    const location = useLocation();
    const currentPath = location.pathname;
    const params = useParams(); 
    const param = Object.values(params)[0];
    const resource = param.split("/")[0];
    const path = param.split("/").slice(0, -1).join("/");
    const dataProvider = useDataProvider();
    const [emailOpen, setEmailOpen] = useState(false);

    const takeScreenshot = async (title) => {
        const canvas = await html2canvas(document.body); // ページ全体をキャプチャ
        const image = canvas.toDataURL("image/png");
        // 画像をダウンロード
        const link = document.createElement("a");
        link.href = image;
        link.download = `${title}.png`;
        link.click();
    };

    // 現在のパスに対応するタイトルを取得（デフォルトタイトルも指定）
    let title = titles[currentPath];
    if(currentPath.includes('/customers') && currentPath.includes('/create')){
        title = "顧客作成";
    }

    // フックを常に呼び出す
    const isEditingUser = currentPath.includes(`/${resource}/`) && !currentPath.includes('/create');
    const id = param.split("/").pop();
    const { data,isLoading } = useGetOne(resource === "reset-password" ? "users" : resource, { id: id}, { enabled: isEditingUser });
   
    if (isEditingUser && data) {
        if(resource.includes("History")){
            title = `${editTitles[path]}：${String(data.number).padStart(5, "0")}`;
        }
        else{
            title = `${editTitles[path]}：${getNameAndCompanyName(data.surname,data.name,data.companyName)}`;
        }
    }

    if(resource.includes("invoice-form")){
      title = "請求書作成";
    }

    const handleBack = () => {
        window.history.back(); // または useNavigate() を使って navigate(-1)
    };

    const CustomAppBar = styled(AppBar)(({ theme }) => ({
        [`& .RaAppBar-menuButton`]: {
            display:"none"
        },
    }));

    return (
        <>
            <CustomAppBar {...props} userMenu={<CustomUserMenu />}>

                <IconButton
                    edge="start"
                    color="inherit"
                    aria-label="back"
                    onClick={handleBack}
                >
                    <ArrowBackIcon />
                </IconButton>
                <Typography
                    variant="h6"
                    color="inherit"
                    style={{ flex: 1, textAlign: 'start' }}
                >
                    {title}
                </Typography>
                <IconButton color="inherit" onClick={() => takeScreenshot(title)}>
                    <CameraAltIcon />
                </IconButton>
                <IconButton color="inherit" onClick={()=>setEmailOpen(true)}>
                    <EmailIcon />
                </IconButton>
            </CustomAppBar>
            <EmailDialog
                open={emailOpen}
                onClose={() => setEmailOpen(false)}
            />
        </>
    );
};

export default HeaderBar;
